import React, { useState, ReactElement } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  SvgIcon,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import SchoolIcon from '@mui/icons-material/School';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ParkIcon from '@mui/icons-material/Park';
import TranslateIcon from '@mui/icons-material/Translate';

interface Department {
  id: string;
  name: {
    mr: string;
    hi: string;
    en: string;
  };
  description: {
    mr: string;
    hi: string;
    en: string;
  };
  icon: ReactElement;
  contact: string;
  email: string;
  image: string;
  content: string;
}

const departments: Department[] = [
  {
    id: 'law-order',
    name: {
      mr: 'कायदा सुव्यवस्था',
      hi: 'कानून व्यवस्था',
      en: 'Law and Order',
    },
    description: {
      mr: 'गावातील कायदा व सुव्यवस्था राखण्यासाठी समर्पित विभाग.',
      hi: 'गांव में कानून और व्यवस्था बनाए रखने के लिए समर्पित विभाग।',
      en: 'Department dedicated to maintaining law and order in the village.',
    },
    icon: <SecurityIcon />,
    contact: '+91 1234567890',
    email: 'law@pargaon.gov.in',
    image: '/images/law-order.jpg',
    content: 'The Law and Order department is responsible for maintaining law and order in the village. They work to prevent crime and ensure that the villagers are safe and secure.',
  },
  {
    id: 'cultural',
    name: {
      mr: 'सांस्कृतिक',
      hi: 'सांस्कृतिक',
      en: 'Cultural',
    },
    description: {
      mr: 'सांस्कृतिक कार्यक्रम आणि परंपरा जपण्यासाठी कार्यरत विभाग.',
      hi: 'सांस्कृतिक कार्यक्रमों और परंपराओं को बढ़ावा देने वाला विभाग।',
      en: 'Department promoting cultural events and traditions.',
    },
    icon: <TheaterComedyIcon />,
    contact: '+91 1234567891',
    email: 'culture@pargaon.gov.in',
    image: '/images/cultural.jpg',
    content: 'The Cultural department is responsible for promoting cultural events and traditions in the village. They work to preserve the cultural heritage of the village and promote it to the younger generation.',
  },
  {
    id: 'education',
    name: {
      mr: 'शैक्षणिक',
      hi: 'शैक्षिक',
      en: 'Education',
    },
    description: {
      mr: 'शैक्षणिक विकास आणि शाळांच्या व्यवस्थापनासाठी कार्यरत विभाग.',
      hi: 'शैक्षिक विकास और स्कूलों के प्रबंधन के लिए कार्यरत विभाग।',
      en: 'Department working for educational development and school management.',
    },
    icon: <SchoolIcon />,
    contact: '+91 1234567892',
    email: 'education@pargaon.gov.in',
    image: '/images/education.jpg',
    content: 'The Education department is responsible for the educational development of the village. They work to improve the quality of education in the village and manage the schools.',
  },
  {
    id: 'revenue',
    name: {
      mr: 'महसूल',
      hi: 'राजस्व',
      en: 'Revenue',
    },
    description: {
      mr: 'गावाच्या आर्थिक व्यवस्थापन आणि महसूल संकलनासाठी कार्यरत विभाग.',
      hi: 'गांव के वित्तीय प्रबंधन और राजस्व संग्रह के लिए कार्यरत विभाग।',
      en: 'Department handling financial management and revenue collection.',
    },
    icon: <AccountBalanceIcon />,
    contact: '+91 1234567893',
    email: 'revenue@pargaon.gov.in',
    image: '/images/revenue.jpg',
    content: 'The Revenue department is responsible for the financial management of the village. They work to collect revenue and manage the finances of the village.',
  },
  {
    id: 'environment',
    name: {
      mr: 'पर्यावरण',
      hi: 'पर्यावरण',
      en: 'Environment',
    },
    description: {
      mr: 'पर्यावरण संरक्षण आणि हरित विकासासाठी कार्यरत विभाग.',
      hi: 'पर्यावरण संरक्षण और हरित विकास के लिए कार्यरत विभाग।',
      en: 'Department working for environmental protection and green development.',
    },
    icon: <ParkIcon />,
    contact: '+91 1234567894',
    email: 'environment@pargaon.gov.in',
    image: '/images/environment.jpg',
    content: 'The Environment department is responsible for protecting the environment and promoting green development in the village. They work to preserve the natural resources of the village and promote sustainable development.',
  },
];

const Departments = () => {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);
  const [language, setLanguage] = useState<'mr' | 'hi' | 'en'>('mr');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = (lang?: 'mr' | 'hi' | 'en') => {
    if (lang) {
      setLanguage(lang);
    }
    setAnchorEl(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Box sx={{ py: 6, backgroundColor: 'background.default' }}>
      <Container maxWidth="lg">
        {/* Header with Language Selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1">
            {language === 'mr' ? 'विभाग' : language === 'hi' ? 'विभाग' : 'Departments'}
          </Typography>
          <IconButton 
            onClick={handleLanguageClick}
            sx={{ ml: 2 }}
            aria-label="change language"
          >
            <TranslateIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => handleLanguageClose()}
          >
            <MenuItem onClick={() => handleLanguageClose('mr')}>मराठी</MenuItem>
            <MenuItem onClick={() => handleLanguageClose('hi')}>हिंदी</MenuItem>
            <MenuItem onClick={() => handleLanguageClose('en')}>English</MenuItem>
          </Menu>
        </Box>

        {/* Department Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="department tabs"
          >
            {departments.map((dept, index) => {
              const IconComponent = () => dept.icon;
              return (
                <Tab
                  key={dept.id}
                  icon={<IconComponent />}
                  label={dept.name[language]}
                  id={`dept-tab-${index}`}
                  aria-controls={`dept-tabpanel-${index}`}
                />
              );
            })}
          </Tabs>
        </Box>

        {/* Department Content */}
        {departments.map((dept, index) => (
          <Box
            role="tabpanel"
            hidden={currentTab !== index}
            id={`dept-tabpanel-${index}`}
            aria-labelledby={`dept-tab-${index}`}
            key={dept.id}
          >
            {currentTab === index && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ mr: 2, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                          {React.cloneElement(dept.icon, { fontSize: 'large' })}
                        </Box>
                        <Typography variant="h5" component="h2">
                          {dept.name[language]}
                        </Typography>
                      </Box>
                      <Typography variant="body1" paragraph>
                        {dept.description[language]}
                      </Typography>
                      <img src={dept.image} alt={dept.name[language]} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
                      <Typography variant="body2" color="text.secondary">
                        Contact: {dept.contact}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Email: {dept.email}
                      </Typography>
                      <Typography variant="body1" paragraph>
                        {dept.content}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>
        ))}
      </Container>
    </Box>
  );
};

export default Departments;
